// ============================================================
// ReydenPlugin — Reyden-X iframe integration
// Reads config from global InitData, sends playback state
// via postMessage to parent window
// ============================================================
import type { IPlugin, PluginContext } from '../core/types';

declare const InitData: Record<string, string | number> | undefined;

export class ReydenPlugin implements IPlugin {
  readonly name = 'reyden';

  private ctx: PluginContext | null = null;
  private unsubs: Array<() => void> = [];
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  init(ctx: PluginContext): void {
    this.ctx = ctx;

    // Parse InitData if available (injected by Reyden-X)
    if (typeof InitData !== 'undefined') {
      this.parseInitData(ctx);
    }

    this.bindEvents();
    this.startHeartbeat();
    ctx.logger.debug('ReydenPlugin initialized');
  }

  destroy(): void {
    this.unsubs.forEach(fn => fn());
    this.unsubs = [];
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.ctx = null;
  }

  // --- Private ---

  private parseInitData(ctx: PluginContext): void {
    if (typeof InitData === 'undefined') return;

    const autoplay = !!Number(InitData['autoplay']);
    const volume = Number(InitData['volume']) / 100;
    const muted = !!Number(InitData['mute']);

    if (autoplay) {
      ctx.store.setState({ volume: Math.max(0, Math.min(1, volume)), isMuted: muted });
    }

    ctx.logger.debug('Reyden InitData parsed:', { autoplay, volume, muted });
  }

  private bindEvents(): void {
    const { eventBus } = this.ctx!;

    this.unsubs.push(
      eventBus.on('playing', () => this.sendEvent('Play')),
      eventBus.on('stopped', () => this.sendEvent('Pause')),
      eventBus.on('ended', () => this.sendEvent('Stop')),
    );
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      if (this.ctx?.store.getState().isPlaying) {
        this.sendEvent('Playing');
      }
    }, 1000);
  }

  private sendEvent(event: string): void {
    try {
      window.parent.postMessage(JSON.stringify({ event }), '*');
    } catch {
      // iframe security restriction
    }
  }
}
